import { useEffect, useState } from 'react'
import './App.css'
import { clearSavedState, fetchRemoteState, loadSavedState, saveReminderSettings, saveState } from './persistence'
import {
  auth,
  cancelPaypalSubscription,
  isFirebaseConfigured,
  linkEmailToCurrentUser,
  loginWithEmail,
  requestTrainingPlan,
  resetPassword,
  signOutUser,
  subscribeToAuthUser,
} from './firebase'
import { toISODate } from './lib/exercise'
import BottomNav from './components/BottomNav'
import ExerciseSheet from './components/ExerciseSheet'
import Onboarding from './screens/Onboarding'
import Login from './screens/Login'
import Signup from './screens/Signup'
import Subscription from './screens/Subscription'
import Membership from './screens/Membership'
import Today from './screens/Today'
import CalendarScreen from './screens/CalendarScreen'
import Challenges from './screens/Challenges'
import Profile from './screens/Profile'

// Marca para el refresh unico de usuarios registrados sin pago. Cambiar el
// sufijo (v2, v3...) vuelve a disparar el refresh una vez para todos.
const UNPAID_REFRESH_KEY = 'unpaid-refresh-done-v1'

function markUnpaidRefreshDone() {
  try {
    localStorage.setItem(UNPAID_REFRESH_KEY, '1')
    return true
  } catch {
    return false
  }
}

function mapAuthError(err) {
  const code = err?.code ?? ''

  if (code.includes('email-already-in-use')) {
    return 'Ese email ya tiene una cuenta. Inicia sesion en su lugar.'
  }
  if (code.includes('weak-password')) {
    return 'La contraseña debe tener al menos 6 caracteres.'
  }
  if (code.includes('invalid-email')) {
    return 'El email no es valido.'
  }
  if (code.includes('wrong-password') || code.includes('invalid-credential') || code.includes('user-not-found')) {
    return 'Email o contraseña incorrectos.'
  }

  return 'Ocurrio un error. Intenta de nuevo.'
}

function createDraft(saved) {
  return {
    fullName: saved?.fullName ?? '',
    age: saved?.age ? String(saved.age) : '',
    weightKg: saved?.weightKg ? String(saved.weightKg) : '',
    heightCm: saved?.heightCm ? String(saved.heightCm) : '',
    level: saved?.level ?? 'principiante',
    daysPerWeek: saved?.daysPerWeek ?? 4,
    goal: saved?.goal ?? '',
  }
}

function defaultWeeklyConfig(profile) {
  return {
    targetSessions: profile?.daysPerWeek ?? 4,
    reminderTime: '06:00',
    reminderText: 'Entrena con disciplina hoy.',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    notificationsEnabled: false,
  }
}

function App() {
  const initial = loadSavedState()
  const [profile, setProfile] = useState(initial?.profile ?? null)
  const [plan, setPlan] = useState(initial?.plan ?? {})
  const [completion, setCompletion] = useState(initial?.completion ?? {})
  const [weeklyConfig, setWeeklyConfig] = useState(initial?.weeklyConfig ?? defaultWeeklyConfig(initial?.profile))
  const [subscription, setSubscription] = useState(initial?.subscription ?? { active: false })
  const [challenges, setChallenges] = useState(initial?.challenges ?? {})
  const [form, setForm] = useState(createDraft(initial?.profile))
  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [activeTab, setActiveTab] = useState('hoy')
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })
  const [sheetDate, setSheetDate] = useState(null)
  const [showMembership, setShowMembership] = useState(false)

  // El pull-to-refresh se bloquea mientras la solapa de detalle esta abierta
  // (tiene su propio scroll y recargaria la pagina): en la app Android via el
  // puente JS y en el navegador cortando el overscroll del documento.
  // La app Android llama a trainerCloseSheet cuando se toca "atras" con la
  // solapa abierta.
  useEffect(() => {
    window.Android?.setRefreshEnabled?.(!sheetDate)
    document.body.style.overscrollBehaviorY = sheetDate ? 'none' : ''

    if (!sheetDate) {
      return undefined
    }
    window.trainerCloseSheet = () => setSheetDate(null)
    return () => {
      delete window.trainerCloseSheet
    }
  }, [sheetDate])

  const [authChecked, setAuthChecked] = useState(!isFirebaseConfigured)
  const [linkedEmail, setLinkedEmail] = useState(null)
  const [showSignup, setShowSignup] = useState(false)
  const [signingUp, setSigningUp] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [loggingIn, setLoggingIn] = useState(false)
  const [resetMessage, setResetMessage] = useState('')
  const [resetting, setResetting] = useState(false)

  useEffect(() => {
    const unsubscribe = subscribeToAuthUser((user) => {
      setLinkedEmail(user?.email ?? null)
      setAuthChecked(true)

      // El estado de la membresia en localStorage se puede editar desde
      // devtools; el de Firestore solo lo activa la Cloud Function. Al
      // iniciar sesion pisamos el local con el remoto.
      if (user) {
        fetchRemoteState(user.uid)
          .then((remote) => {
            if (remote) {
              setSubscription(remote.subscription)
            }
          })
          .catch(() => {})
      }
    })
    return unsubscribe
  }, [])

  const [planError, setPlanError] = useState('')
  const [planRequestId, setPlanRequestId] = useState(0)

  // El plan ya no se arma en el cliente: con perfil y membresia activa se
  // pide a la Cloud Function, que comprueba la membresia en el servidor.
  const isReady = profile !== null
  const hasPlan = Object.keys(plan).length > 0
  // El plan cubre 42 dias. Cuando ya no quedan fechas desde hoy en adelante
  // se pide uno nuevo; mientras tanto se sigue mostrando el anterior.
  const todayIso = toISODate(new Date())
  const planExpired = hasPlan && !Object.keys(plan).some((date) => date >= todayIso)
  const needsPlan = !hasPlan || planExpired

  useEffect(() => {
    if (!profile || !subscription.active || !needsPlan) {
      return
    }

    let cancelled = false

    requestTrainingPlan({
      level: profile.level,
      daysPerWeek: profile.daysPerWeek,
      startDate: toISODate(new Date()),
    })
      .then((generatedPlan) => {
        if (cancelled) {
          return
        }
        setPlan(generatedPlan)
        void saveState(profile, generatedPlan, completion, weeklyConfig, subscription, challenges)
      })
      .catch(() => {
        if (!cancelled) {
          setPlanError('No pudimos generar tu plan. Revisa tu conexion e intenta de nuevo.')
        }
      })

    return () => {
      cancelled = true
    }
    // completion/weeklyConfig/challenges solo se reenvian al guardar; no
    // deben volver a disparar la generacion del plan.
  }, [profile, subscription, needsPlan, planRequestId])

  // Refresh unico para usuarios con cuenta que todavia no pagaron: trae el
  // estado de Firestore (por si la membresia se activo del lado del servidor
  // o manualmente) y recarga la app. La marca se escribe ANTES de recargar
  // para que nunca entre en loop; si localStorage no anda, no se recarga.
  useEffect(() => {
    if (!isFirebaseConfigured || !linkedEmail || !isReady || subscription.active || loggingIn || signingUp) {
      return
    }

    let alreadyDone = true
    try {
      alreadyDone = localStorage.getItem(UNPAID_REFRESH_KEY) === '1'
    } catch {
      // sin localStorage no podemos garantizar "una sola vez"
    }
    if (alreadyDone || !markUnpaidRefreshDone()) {
      return
    }

    const uid = auth?.currentUser?.uid
    void (async () => {
      try {
        const remote = uid ? await fetchRemoteState(uid) : null
        if (remote) {
          await saveState(
            remote.profile,
            remote.plan,
            remote.completion,
            remote.weeklyConfig,
            remote.subscription,
            remote.challenges,
          )
        }
      } catch {
        // si falla Firestore igual recargamos con lo que haya local
      } finally {
        window.location.reload()
      }
    })()
  }, [linkedEmail, isReady, subscription.active, loggingIn, signingUp])

  function updateField(key, value) {
    setForm((old) => ({ ...old, [key]: value }))
  }

  function nextStep() {
    setError('')

    if (step === 0 && !form.fullName.trim()) {
      setError('Escribe tu nombre para continuar.')
      return
    }

    if (step === 1) {
      const age = Number(form.age)
      const weight = Number(form.weightKg)
      const height = Number(form.heightCm)
      if (!age || !weight || !height) {
        setError('Completa edad, peso y altura con numeros.')
        return
      }
    }

    if (step < 2) {
      setStep((old) => old + 1)
    }
  }

  function prevStep() {
    setStep((old) => old - 1)
  }

  async function buildAndSaveProfile(profileForm) {
    const builtProfile = {
      fullName: profileForm.fullName.trim(),
      age: Number(profileForm.age),
      weightKg: Number(profileForm.weightKg),
      heightCm: Number(profileForm.heightCm),
      level: profileForm.level,
      daysPerWeek: profileForm.daysPerWeek,
      goal: profileForm.goal.trim(),
      createdAt: new Date().toISOString(),
    }

    const builtWeeklyConfig = defaultWeeklyConfig(builtProfile)

    await saveState(builtProfile, {}, {}, builtWeeklyConfig, subscription, challenges)
    setProfile(builtProfile)
    setPlan({})
    setCompletion({})
    setWeeklyConfig(builtWeeklyConfig)
  }

  async function finishOnboarding() {
    setError('')

    if (!form.goal.trim()) {
      setError('Describe tu objetivo principal.')
      return
    }

    try {
      setSaving(true)
      await buildAndSaveProfile(form)
    } catch {
      setError('No pude guardar en Firebase. Sigue funcionando localmente.')
    } finally {
      setSaving(false)
    }
  }

  async function finishSignup(signupForm) {
    setError('')
    setSigningUp(true)

    try {
      await linkEmailToCurrentUser(signupForm.email.trim(), signupForm.password)
    } catch (err) {
      setError(mapAuthError(err))
      setSigningUp(false)
      return
    }

    try {
      await buildAndSaveProfile(signupForm)
      markUnpaidRefreshDone()
      setShowSignup(false)
    } catch {
      setError('No pude guardar en Firebase. Sigue funcionando localmente.')
    } finally {
      setSigningUp(false)
    }
  }

  function toggleExercise(date, exerciseName) {
    setCompletion((old) => {
      const key = `${date}__${exerciseName}`
      const updated = { ...old, [key]: !old[key] }
      void saveState(profile, plan, updated, weeklyConfig, subscription, challenges)
      return updated
    })
  }

  function toggleChallenge(challengeId) {
    setChallenges((old) => {
      const updated = { ...old, [challengeId]: !old[challengeId] }
      void saveState(profile, plan, completion, weeklyConfig, subscription, updated)
      return updated
    })
  }

  async function handleLogin(email, password) {
    setLoginError('')
    setResetMessage('')
    setLoggingIn(true)
    try {
      const user = await loginWithEmail(email, password)
      const remote = await fetchRemoteState(user.uid)
      if (!remote) {
        setLoginError('No encontramos datos guardados para esta cuenta.')
        return
      }

      markUnpaidRefreshDone()
      setProfile(remote.profile)
      setPlan(remote.plan)
      setCompletion(remote.completion)
      setWeeklyConfig(remote.weeklyConfig ?? defaultWeeklyConfig(remote.profile))
      setSubscription(remote.subscription)
      setChallenges(remote.challenges ?? {})
      await saveState(
        remote.profile,
        remote.plan,
        remote.completion,
        remote.weeklyConfig,
        remote.subscription,
        remote.challenges,
      )
    } catch (err) {
      setLoginError(mapAuthError(err))
    } finally {
      setLoggingIn(false)
    }
  }

  async function handleForgotPassword(email) {
    setLoginError('')
    setResetMessage('')

    if (!email.trim()) {
      setLoginError('Escribe tu email para recuperar la contraseña.')
      return
    }

    setResetting(true)
    try {
      await resetPassword(email.trim())
      setResetMessage('Si existe una cuenta con ese email, te enviamos un enlace para restablecer tu contraseña.')
    } catch (err) {
      const code = err?.code ?? ''
      // user-not-found se trata como exito para no revelar que emails tienen
      // cuenta (enumeracion de usuarios).
      if (code.includes('user-not-found')) {
        setResetMessage('Si existe una cuenta con ese email, te enviamos un enlace para restablecer tu contraseña.')
      } else if (code.includes('invalid-email')) {
        setLoginError('El email no es valido.')
      } else {
        setLoginError('No pude enviar el email. Intenta de nuevo.')
      }
    } finally {
      setResetting(false)
    }
  }

  function handleSubscribed({ subscriptionId, startedAt }) {
    const updated = { active: true, provider: 'paypal', subscriptionId, startedAt }
    setSubscription(updated)
    void saveState(profile, plan, completion, weeklyConfig, updated, challenges)
  }

  async function handleCancelSubscription() {
    const confirmed = window.confirm(
      'Esto cancela el cobro en PayPal y perdes el acceso al calendario y seguimiento diario. Continuar?',
    )
    if (!confirmed) {
      return
    }

    try {
      const { canceledAt } = await cancelPaypalSubscription()
      const updated = { ...subscription, active: false, canceledAt }
      setSubscription(updated)
      void saveState(profile, plan, completion, weeklyConfig, updated, challenges)
      setShowMembership(false)
    } catch {
      window.alert('No pudimos cancelar la suscripcion. Intenta de nuevo en unos minutos.')
    }
  }

  async function handleSaveReminders(reminderConfig) {
    const updated = { ...weeklyConfig, ...reminderConfig }
    setWeeklyConfig(updated)
    void saveState(profile, plan, completion, updated, subscription, challenges)
    // getFcmToken lo expone la app Android (puente JS) solo en el dominio propio.
    const fcmToken = window.Android?.getFcmToken?.() || null
    await saveReminderSettings(updated, fcmToken)
  }

  function openDay(date) {
    if (!plan[date]) {
      return
    }

    setSheetDate(date)
  }

  function resetPlan() {
    const confirmed = window.confirm('Esto borra tu perfil y plan actual. Podras crear uno nuevo. Continuar?')
    if (!confirmed) {
      return
    }

    clearSavedState()
    setProfile(null)
    setPlan({})
    setCompletion({})
    setSubscription({ active: false })
    setChallenges({})
    setForm(createDraft())
    setStep(0)
    setActiveTab('hoy')
  }

  if (isFirebaseConfigured && !authChecked) {
    return (
      <main className="screen onboarding-screen">
        <p className="subtitle">Cargando...</p>
      </main>
    )
  }

  // loggingIn/signingUp se mantienen despues de que linkedEmail ya cambio
  // (el listener de auth dispara antes de que terminen de cargar perfil/plan
  // remotos), para no mostrar un flash del wizard vacio entre medio.
  if (isFirebaseConfigured && (!linkedEmail || loggingIn || signingUp)) {
    return showSignup ? (
      <Signup onFinish={finishSignup} saving={signingUp} error={error} onBackToLogin={() => setShowSignup(false)} />
    ) : (
      <Login
        onLogin={handleLogin}
        loginError={loginError}
        loggingIn={loggingIn}
        onForgotPassword={handleForgotPassword}
        resetMessage={resetMessage}
        resetting={resetting}
        onGoToSignup={() => setShowSignup(true)}
      />
    )
  }

  if (!isReady) {
    return (
      <Onboarding
        form={form}
        step={step}
        error={error}
        saving={saving}
        updateField={updateField}
        nextStep={nextStep}
        prevStep={prevStep}
        finishOnboarding={finishOnboarding}
      />
    )
  }

  if (!subscription.active) {
    return (
      <Subscription
        profile={profile}
        onSubscribed={handleSubscribed}
        onBack={isFirebaseConfigured ? () => void signOutUser() : undefined}
      />
    )
  }

  if (!hasPlan) {
    return (
      <main className="screen onboarding-screen">
        {planError ? (
          <section className="onboarding-card">
            <p className="error-text">{planError}</p>
            <button
              type="button"
              className="primary full-width"
              onClick={() => {
                setPlanError('')
                setPlanRequestId((old) => old + 1)
              }}
            >
              Reintentar
            </button>
          </section>
        ) : (
          <p className="subtitle">Generando tu plan...</p>
        )}
      </main>
    )
  }

  if (showMembership) {
    return (
      <Membership
        subscription={subscription}
        onCancel={handleCancelSubscription}
        onBack={() => setShowMembership(false)}
      />
    )
  }

  return (
    <div className="app-shell">
      {activeTab === 'hoy' && (
        <Today profile={profile} plan={plan} completion={completion} onToggle={toggleExercise} onOpenDetail={openDay} />
      )}

      {activeTab === 'calendario' && (
        <CalendarScreen
          plan={plan}
          completion={completion}
          viewMonth={viewMonth}
          setViewMonth={setViewMonth}
          onOpenDay={openDay}
        />
      )}

      {activeTab === 'desafios' && (
        <Challenges plan={plan} completion={completion} challenges={challenges} onToggleChallenge={toggleChallenge} />
      )}

      {activeTab === 'perfil' && (
        <Profile
          profile={profile}
          plan={plan}
          completion={completion}
          subscription={subscription}
          onReset={resetPlan}
          isFirebaseConfigured={isFirebaseConfigured}
          linkedEmail={linkedEmail}
          onOpenMembership={() => setShowMembership(true)}
          weeklyConfig={weeklyConfig}
          onSaveReminders={handleSaveReminders}
        />
      )}

      <BottomNav active={activeTab} onChange={setActiveTab} />

      {sheetDate && plan[sheetDate] && (
        <ExerciseSheet
          key={sheetDate}
          date={sheetDate}
          dayPlan={plan[sheetDate]}
          completion={completion}
          onToggle={toggleExercise}
          onClose={() => setSheetDate(null)}
        />
      )}
    </div>
  )
}

export default App
